import { FC, useEffect, useMemo, useRef, useState } from 'react';

import {
  INewPolicyRulePayload,
  IPolicyRule,
  IPolicySettingsField,
  useCreateRuleMutation,
  useUpdateRuleMutation,
  useGetPolicySettingsQuery,
} from '@entities/policy';
import { Stack, Typography } from '@mui/material';
import { FormItemWithError } from '@shared/components/Form';

import PolicyRuleParamsSection from './PolicyRuleParamsSection';
import { RuleParam } from './policyRuleTypes';
import { buildParamsFromRule, serializeParams } from './policyRuleUtils';

interface IProps {
  rule?: IPolicyRule;
  onCancel?: () => void;
  policyId: string;
}

const PolicyRuleForm: FC<IProps> = ({ rule, onCancel, policyId }) => {
  const { data: policyRuleSettings } = useGetPolicySettingsQuery();
  const [createRule, { isLoading: isCreating, isError: isCreateError, error: createError }] =
    useCreateRuleMutation();
  const [updateRule, { isLoading: isUpdating, isError: isUpdateError, error: updateError }] =
    useUpdateRuleMutation();
  const [name, setName] = useState(rule?.name ?? '');
  const [description, setDescription] = useState(rule?.description ?? '');
  const [conditions, setConditions] = useState<RuleParam[]>([]);
  const [selections, setSelections] = useState<RuleParam[]>([]);
  const [minAmount, setMinAmount] = useState('0');
  const [maxAmount, setMaxAmount] = useState('0');
  const shouldScrollConditionsRef = useRef(false);
  const shouldScrollSelectionsRef = useRef(false);
  const lastConditionRef = useRef<HTMLDivElement | null>(null);
  const lastSelectionRef = useRef<HTMLDivElement | null>(null);

  const strategyOptions = useMemo(() => {
    return Object.entries(policyRuleSettings?.data?.strategy ?? {}).map(([key, value]) => ({
      value: key,
      label: value,
    }));
  }, [policyRuleSettings?.data?.strategy]);
  const [strategy, setStrategy] = useState('');

  useEffect(() => {
    if (rule?.sort_strategy) {
      setStrategy(rule.sort_strategy);
      return;
    }
    if (strategyOptions.length > 0) {
      setStrategy(strategyOptions[0]?.value ?? '');
    }
  }, [strategyOptions, rule?.sort_strategy]);

  useEffect(() => {
    if (!rule) return;
    setName(rule.name ?? '');
    setDescription(rule.description ?? '');
    setMinAmount(String(rule.min ?? '0'));
    setMaxAmount(String(rule.max ?? '0'));

    const conditionFields = policyRuleSettings?.data?.conditions ?? [];
    const selectionFields = policyRuleSettings?.data?.selections ?? [];

    setConditions(buildParamsFromRule(rule.conditions, conditionFields));
    setSelections(buildParamsFromRule(rule.selections, selectionFields));
  }, [rule, policyRuleSettings?.data?.conditions, policyRuleSettings?.data?.selections]);

  const availableConditionOptions = useMemo(() => {
    const conditionOptions = policyRuleSettings?.data?.conditions ?? [];
    return conditionOptions.filter(opt => !conditions.some(c => c.techname === opt.techname));
  }, [policyRuleSettings?.data?.conditions, conditions]);

  const availableSelectionOptions = useMemo(() => {
    const selectionOptions = policyRuleSettings?.data?.selections ?? [];
    return selectionOptions.filter(opt => !selections.some(s => s.techname === opt.techname));
  }, [policyRuleSettings?.data?.selections, selections]);

  const addParam = (
    list: RuleParam[],
    setter: (next: RuleParam[]) => void,
    option: IPolicySettingsField,
  ) => {
    if (!option) return;
    if (option.type === 'multi-select') {
      setter([
        ...list,
        {
          techname: option.techname,
          label: option.label,
          type: 'multi-select',
          values: option.values,
          selected: [],
        },
      ]);
      return;
    }

    setter([
      ...list,
      {
        techname: option.techname,
        label: option.label,
        type: 'digit-compare',
        operator: '=',
        value: '',
      },
    ]);
  };

  useEffect(() => {
    if (shouldScrollConditionsRef.current && lastConditionRef.current) {
      lastConditionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      shouldScrollConditionsRef.current = false;
    }
  }, [conditions.length]);

  useEffect(() => {
    if (shouldScrollSelectionsRef.current && lastSelectionRef.current) {
      lastSelectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      shouldScrollSelectionsRef.current = false;
    }
  }, [selections.length]);

  const handleSubmit = async () => {
    const payload: INewPolicyRulePayload = {
      name,
      description,
      policy_id: String(policyId),
      min: Number(minAmount),
      max: Number(maxAmount),
      conditions: serializeParams(conditions),
      selections: serializeParams(selections),
      sort_strategy: strategy,
      active: true,
    };
    try {
      if (rule) {
        await updateRule({ id: rule.id, ...payload }).unwrap();
      } else {
        await createRule(payload).unwrap();
      }
      onCancel?.();
    } catch (error) {
      console.error(error);
    }
  };

  const isError = rule ? isUpdateError : isCreateError;
  const mutationError = rule ? updateError : createError;

  return (
    <form
      className='modal-sticky-layout'
      style={{ minWidth: 520 }}
      onSubmit={e => {
        e.preventDefault();
        handleSubmit();
      }}
    >
      <div className='modal-sticky-header'>
        <Typography variant='h6' fontWeight={700}>
          {rule ? 'Edit Rule' : 'Add Rule'}
        </Typography>
      </div>

      <div className='modal-sticky-content'>
        <Stack spacing={2}>
          <FormItemWithError name='name' label='Name' isError={isError} error={mutationError}>
            <input
              id='name'
              placeholder='Rule Name'
              type='text'
              name='name'
              value={name}
              onChange={e => setName(e.target.value)}
            />
          </FormItemWithError>
          <FormItemWithError
            name='description'
            label='Description'
            isError={isError}
            error={mutationError}
          >
            <textarea
              id='description'
              name='description'
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder='Description'
            />
          </FormItemWithError>

          <div ref={conditions.length ? lastConditionRef : null}>
            <PolicyRuleParamsSection
              title='1. Condition'
              items={conditions}
              available={availableConditionOptions}
              onAdd={opt => {
                shouldScrollConditionsRef.current = true;
                addParam(conditions, setConditions, opt);
              }}
              onRemove={techname =>
                setConditions(prev => prev.filter(c => c.techname !== techname))
              }
              onUpdate={next => setConditions(next)}
            />
          </div>

          <div ref={selections.length ? lastSelectionRef : null}>
            <PolicyRuleParamsSection
              title='2. Selection'
              items={selections}
              available={availableSelectionOptions}
              onAdd={opt => {
                shouldScrollSelectionsRef.current = true;
                addParam(selections, setSelections, opt);
              }}
              onRemove={techname =>
                setSelections(prev => prev.filter(c => c.techname !== techname))
              }
              onUpdate={next => setSelections(next)}
            />
          </div>

          <Stack spacing={2}>
            <Typography variant='subtitle2' fontWeight={600}>
              3. Limits
            </Typography>
            <Stack className='min-max' sx={{ width: '100%' }} direction='row' spacing={2}>
              <FormItemWithError
                name='min'
                label='Min Amount'
                isError={isError}
                error={mutationError}
              >
                <input
                  id='min'
                  placeholder='Min Amount'
                  type='number'
                  min={0}
                  value={minAmount}
                  onChange={e => setMinAmount(e.target.value)}
                />
              </FormItemWithError>
              <FormItemWithError
                name='max'
                label='Max Amount'
                isError={isError}
                error={mutationError}
              >
                <input
                  className='full-width'
                  id='max'
                  placeholder='Max Amount'
                  type='number'
                  min={Number(minAmount)}
                  value={maxAmount}
                  onChange={e => setMaxAmount(e.target.value)}
                />
              </FormItemWithError>
            </Stack>
          </Stack>

          <Stack spacing={2}>
            <Typography variant='subtitle2' fontWeight={600}>
              4. Strategy
            </Typography>
            <FormItemWithError
              name='sort_strategy'
              label='Strategy Type'
              isError={isError}
              error={mutationError}
            >
              <select
                value={strategy}
                onChange={e => {
                  setStrategy(e.target.value);
                }}
              >
                {strategyOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </FormItemWithError>
          </Stack>
        </Stack>
      </div>

      <div className='modal-sticky-footer'>
        <Stack direction='row' spacing={1} justifyContent='flex-end'>
          <button type='button' onClick={onCancel} className='secondary-btn'>
            Cancel
          </button>
          <button type='submit' className='primary-btn' disabled={isCreating || isUpdating}>
            {isCreating || isUpdating ? 'Saving...' : 'Save Rule'}
          </button>
        </Stack>
      </div>
    </form>
  );
};

export default PolicyRuleForm;
